import type {
  JobProgression,
  ProgressionFlow,
  ProgressionStepStatus,
} from '@/features/jobs/taxes/progression/JobProgression'

/**
 * Progression step content.
 */
export const progressionStepDescriptors: Record<
  ProgressionStepStatus,
  ProgressionStepContentDescriptor
> = {
  // This is the default step.
  NotStarted: {
    progress: { completed: 0, pending: 20 },
    title: 'Document review',
    listTitle: 'Your Pro is reviewing your information',
    actionText: 'Chat with your Pro',
    description:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    visibility: 'Always',
    flows: {
      Retail: {},
      Review: {},
      Outsourcing: {},
      BusinessExtension: {},
      PersonalExtension: {},
      Essential: {
        title: 'Job submitted',
        description:
          'A Pro will begin working on the job shortly. At this stage, we can no longer accept any documents.',
      },
      Preparation: {
        progress: { completed: 0, pending: 33 },
      },
    },
  },

  JobStarted: {
    title: 'Draft preparation',
    listTitle: 'A draft return is being prepared',
    actionText: 'Chat with your Pro',
    description:
      'A draft return is being prepared. When ready, your Pro will contact you to review.',
    progress: { completed: 20, pending: 20 },
    visibility: 'Always',
    flows: {
      Retail: {},
      Outsourcing: {},
      Essential: {
        title: 'Job in progress',
        description: 'The deliverable is being prepared.',
        progress: { completed: 33, pending: 33 },
      },
      Preparation: {
        description:
          'Your return is being prepared. Once your Pro has finished, they will upload their preparation notes.',
        progress: { completed: 40, pending: 40 },
      },
      Review: {
        description:
          'Your return is being reviewed. When ready, your Pro will contact you to review the draft return.',
      },
    },
  },

  OpenItems: {
    title: 'Documents missing',
    listTitle: 'Action required: Upload missing documents',
    actionText: 'Upload documents',
    description:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 0, pending: 20 },
    visibility: 'WhenCurrent',
    flows: {
      Retail: {},
      Review: {},
      Outsourcing: {},
      Preparation: {
        progress: { completed: 0, pending: 40 },
      },
    },
  },

  DraftInReview: {
    title: 'Draft review',
    listTitle: 'Action required: Review your draft return',
    actionText: 'Review draft',
    description:
      'Your Pro has finalized a draft return for review. Please review and notify your Pro if any changes are necessary.',
    progress: { completed: 40, pending: 20 },
    visibility: 'Always',
    flows: {
      Retail: {},
      Review: {},
      Outsourcing: {
        progress: { completed: 66, pending: 20 },
      },
    },
  },

  DraftRejected: {
    title: 'Changes requested',
    listTitle: 'Your Pro is making changes',
    actionText: 'Chat with your Pro',
    description:
      'You requested changes to the return. Once revised, your Pro will request another draft approval from you.',
    progress: { completed: 40, pending: 20 },
    visibility: 'WhenCurrent',
    flows: {
      Retail: {},
      Review: {},
      Outsourcing: {
        progress: { completed: 50, pending: 20 },
      },
    },
  },

  DraftApproved: {
    title: 'Draft approved',
    listTitle: 'Your Pro is preparing your final documents',
    actionText: 'Chat with your Pro',
    description:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 50, pending: 20 },
    visibility: 'Always',
    flows: {
      Retail: {},
      Outsourcing: {},
      Review: {
        progress: { completed: 60, pending: 20 },
      },
    },
  },

  AuthorizationRequested: {
    title: 'Authorization requested',
    listTitle: 'Authorization requested',
    actionText: 'Chat with your Pro',
    description:
      'Your Pro has completed your return. Please sign the authorization form and notify your Pro in chat when completed.',
    progress: { completed: 60, pending: 20 },
    visibility: 'WhenReached',
    flows: {
      Retail: {},
    },
  },

  AuthorizationSigned: {
    title: 'Authorization received',
    listTitle: 'Your Pro is finalizing your return',
    actionText: 'Chat with your Pro',
    description:
      'Your Pro will now e-file your return. Please wait for confirmation.',
    progress: { completed: 70, pending: 20 },
    visibility: 'Always',
    flows: {
      Retail: {},
    },
  },

  ReturnFiled: {
    title: 'Return filed',
    listTitle: 'Your return is complete and ready to view',
    actionText: 'View return',
    description:
      'Good news! Your Pro has filed your return, and will now proceed to complete the job.',
    progress: { completed: 80, pending: 20 },
    visibility: 'Always',
    flows: {
      Retail: {},
    },
  },

  DeliverableCompleted: {
    title: 'Job completed',
    listTitle: 'Job completed',
    actionText: 'See details',
    description: 'Your Pro has completed the work and will close the job soon.',
    progress: { completed: 67, pending: 33 },
    visibility: 'Always',
    flows: {
      Essential: {},
    },
  },

  PreparationCompleted: {
    title: 'Preparation complete',
    listTitle: 'Preparation complete',
    actionText: 'See details',
    description:
      'Your Pro has completed the preparation for your tax return. The preparation notes can be found in the Documents section.',
    progress: { completed: 80, pending: 20 },
    visibility: 'Always',
    flows: {
      Preparation: {},
    },
  },

  ReviewCompleted: {
    title: 'Review completed',
    listTitle: 'Review completed',
    actionText: 'See details',
    description:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 80, pending: 20 },
    visibility: 'Always',
    flows: {
      Review: {},
    },
  },

  ExtensionInProgress: {
    title: 'Extension in progress',
    listTitle: 'Extension in progress',
    actionText: 'See details',
    description:
      'Your extension is being prepared. Once your Pro has finished, they will upload the e-file confirmation.',
    progress: { completed: 20, pending: 20 },
    visibility: 'Always',
    flows: {
      PersonalExtension: {},
      BusinessExtension: {},
      OutsourcingExtension: {},
    },
  },

  ExtensionConfirmed: {
    title: 'Extension status',
    listTitle: 'Extension status',
    actionText: 'See details',
    description:
      'Your Pro has filed an extension on your behalf. You will receive an update when the IRS has either accepted or rejected the extension.',
    progress: { completed: 40, pending: 20 },
    visibility: 'Always',
    flows: {
      PersonalExtension: {},
      BusinessExtension: {},
    },
  },

  ExtensionAccepted: {
    title: 'Extension accepted',
    listTitle: 'Extension accepted',
    actionText: 'See details',
    description: 'The extension was successfully accepted by the IRS.',
    progress: { completed: 80, pending: 20 },
    visibility: 'Always',
    flows: {
      PersonalExtension: {},
      BusinessExtension: {},
    },
  },

  ExtensionRejected: {
    title: 'Extension rejected',
    listTitle: 'Extension rejected',
    actionText: 'See details',
    description:
      'The extension was rejected by the IRS. This could be due to an already filed extension on your behalf. Your Pro can answer any questions you might have.',
    progress: { completed: 80, pending: 20 },
    visibility: 'WhenReached',
    flows: {
      PersonalExtension: {},
      BusinessExtension: {},
    },
  },

  ProgressionWaitingOnClient: {
    title: 'Resume job when ready',
    listTitle: 'Resume job when ready',
    actionText: 'See details',
    description:
      'Please notify your Pro when you’re ready to resume your tax return.',
    progress: { completed: 85, pending: 15 },
    visibility: 'Always',
    flows: {
      PersonalExtension: {},
      BusinessExtension: {},
      OutsourcingExtension: {},
    },
  },

  ReadyToFile: {
    title: 'Extension completed',
    listTitle: 'Extension completed',
    actionText: 'See details',
    description: 'The extension was successfully completed by your Pro.',
    progress: { completed: 100, pending: 0 },
    visibility: 'Always',
    flows: {
      OutsourcingExtension: {},
    },
  },
}

/**
 * All progression step statuses as an array.
 */
const allProgressionStepStatuses = Object.keys(
  progressionStepDescriptors,
) as ProgressionStepStatus[]

/**
 * Tracks the job progression completion at a step
 */
export interface ProgressionCompletion {
  /**
   * Value between 0 and 100 representing how much of
   * the progression has been considered completed.
   */
  readonly completed: number

  /**
   * Value between 0 and 100 (but the sum of the value + {@link ProgressionCompletion.completed} not exceeding 100)
   * representing how much of the overall progression completing this step will
   * contribute to the overall completion.
   */
  readonly pending: number
}

/**
 * Whether the step should be always visible, only when reached, or only when current.
 */
export type ProgressionStepVisibility = 'Always' | 'WhenReached' | 'WhenCurrent'

/**
 * Describes content for a progression step, including overrides
 * for specific progression flows.
 */
export interface ProgressionStepContentDescriptor {
  /**
   * The step's default title.
   */
  readonly title: string

  /**
   * The step's title displayed on the job card in the job list.
   */
  readonly listTitle: string

  /**
   * The step's default action text, which is the text shown in the job card.
   */
  readonly actionText: string

  /**
   * The step's default description.
   */
  readonly description: string

  /**
   * The default progression completion for the step.
   */
  readonly progress: ProgressionCompletion

  /**
   * The step's visibility.
   */
  readonly visibility: ProgressionStepVisibility

  /**
   * Which flows the step is applicable for, as well as flow-specific
   * overrides for the step's properties. An empty object indicates
   * opting the step into the flow with the default properties.
   */
  readonly flows?: Partial<
    Record<ProgressionFlow, ProgressionStepContentOverrideDescriptor>
  >
}

/**
 * Describes an override for a progression step.
 */
export interface ProgressionStepContentOverrideDescriptor {
  /**
   * Overridden step title for a flow.
   */
  readonly title?: string

  /**
   * Overridden step action text for a flow.
   */
  readonly actionText?: string

  /**
   * Overridden step description for a flow.
   */
  readonly description?: string

  /**
   * Overridden progression completion for a flow.
   */
  readonly progress?: Partial<ProgressionCompletion>
}

/**
 * Computed content for a progression step.
 */
export interface ComputedProgressionStepContent {
  /**
   * Effective title of the step.
   */
  readonly title: string

  /**
   * Effective title of the step displayed on the job card in the job list.
   */
  readonly listTitle: string

  /**
   * Effective description of the step.
   */
  readonly description: string

  /**
   * Effective action text for the step.
   */
  readonly actionText: string

  /**
   * Effective progression completion for the step.
   */
  readonly progress: ProgressionCompletion

  /**
   * The step's visibility.
   */
  readonly visibility: ProgressionStepVisibility
}

/**
 * A computed step in a particular flow.
 */
export interface ComputedProgressionFlowStep {
  /**
   * The corresponding step status.
   */
  readonly step?: ProgressionStepStatus

  /**
   * The progression step content for the flow.
   */
  readonly content: ComputedProgressionStepContent

  /**
   * Whether the step has been completed.
   */
  readonly state: ComputedProgressionFlowStepState
}

/**
 * The state of a computed progression flow step.
 */
export type ComputedProgressionFlowStepState =
  | 'Completed'
  | 'Current'
  | 'Upcoming'

/**
 * Options for {@link computeProgressionFlow}.
 */
export interface GetComputedProgressionFlowOptions {
  /**
   * The job's progression flow, if any.
   */
  flow?: ProgressionFlow

  /**
   * All recorded steps in the progression flow, if any.
   */
  steps: ProgressionStepStatus[]

  /**
   * Whether the job is an extension-only job.
   */
  isExtensionOnlyJob: boolean

  /**
   * If `true`, then every possible step in the flow is returned.
   * For example, "Draft Rejected" has visibility configured, so if
   * this is set to `true`, that step would always be returned for flows
   * that include it.
   */
  ignoreVisibility?: boolean
}

/**
 * Options for {@link getProgressionStepContent}.
 */
export interface GetProgressionStepContentOptions {
  /**
   * The job's progression flow, if any.
   */
  flow?: ProgressionFlow

  /**
   * The current step in the progression flow, if any.
   */
  step?: ProgressionStepStatus
}

/**
 * Gets the progression step content for a flow.
 * Returns `null` if the step is not relevant for the flow.
 *
 * @param options
 */
export function getProgressionStepContent(
  options: GetProgressionStepContentOptions,
): ComputedProgressionStepContent | null {
  const { flow, step } = options
  if (!flow || !step) {
    // No progression flow
    return null
  }

  const rootDescriptor = progressionStepDescriptors[step]
  const flowOptIn = rootDescriptor.flows?.[flow]
  if (!flowOptIn) {
    return null
  }

  return {
    title: flowOptIn.title ?? rootDescriptor.title,
    listTitle: flowOptIn.title ?? rootDescriptor.listTitle,
    actionText: flowOptIn.actionText ?? rootDescriptor.actionText,
    description: flowOptIn.description ?? rootDescriptor.description,
    progress: {
      ...rootDescriptor.progress,
      ...flowOptIn.progress,
    },
    visibility: rootDescriptor.visibility,
  }
}

/**
 * Checks if the given progression flow is an extension flow.
 */
export function isExtensionFlow(flow: ProgressionFlow) {
  switch (flow) {
    case 'BusinessExtension':
    case 'PersonalExtension':
    case 'OutsourcingExtension':
      return true
    default:
      return false
  }
}

/**
 * Gets the computed progression flow.
 */
export function computeProgressionFlow({
  flow,
  steps,
  isExtensionOnlyJob,
  ignoreVisibility,
}: GetComputedProgressionFlowOptions): ComputedProgressionFlowStep[] {
  if (!flow) {
    return []
  }

  if (isExtensionFlow(flow)) {
    return computeExtensionProgressionFlowCore(
      flow,
      steps,
      isExtensionOnlyJob,
      ignoreVisibility ?? false,
    )
  }

  return computeProgressionFlowCore(flow, steps, ignoreVisibility ?? false)
}

/**
 * Gets the content for the current step in the progression flow.
 */
export function getCurrentStepContent(
  progression: JobProgression,
  steps: ComputedProgressionFlowStep[],
) {
  if (isExtensionFlow(progression.flow)) {
    return getComputedExtensionCurrentStepContent(progression.current, steps)
  }

  const step = steps.find((s) => s.step === progression.current)
  return step?.content ?? null
}

/**
 * Computes the progression flow for a given flow and steps.
 */
function computeProgressionFlowCore(
  flow: ProgressionFlow,
  steps: ProgressionStepStatus[],
  ignoreVisibility: boolean,
) {
  const progressionSteps: ProgressionStepStatus[] =
    steps.length > 0 ? steps : ['NotStarted']
  const currentStep = progressionSteps[progressionSteps.length - 1]

  // Flat-mapping acts as a filter.
  const computedContentDescriptors = allProgressionStepStatuses.flatMap((s) => {
    const content = getProgressionStepContent({
      flow,
      step: s,
    })
    if (!content) {
      return []
    }

    // Only check visibility if not ignoring.
    if (!ignoreVisibility) {
      // Don't include steps that are hidden until current.
      if (content.visibility === 'WhenCurrent' && s !== currentStep) {
        return []
      }

      // Don't include steps that are hidden until reached.
      if (
        content.visibility === 'WhenReached' &&
        !progressionSteps.includes(s)
      ) {
        return []
      }
    }

    return [
      {
        step: s as typeof s | undefined,
        content,
      },
    ]
  })

  // Sort by progression completion
  computedContentDescriptors.sort(
    (a, b) => a.content.progress.completed - b.content.progress.completed,
  )

  // The next state of the step being mapped.
  // This little manoeuvre will save us an additional loop to find the
  // index.
  let state: ComputedProgressionFlowStepState = 'Completed'
  return computedContentDescriptors.map((descriptor) => {
    if (state === 'Completed' && descriptor.step === currentStep) {
      state = 'Current'
    } else if (state === 'Current') {
      state = 'Upcoming'
    }
    return {
      step: descriptor.step,
      content: descriptor.content,
      state: state,
    }
  })
}

/**
 * Computes the progression flow for extension-related steps.
 */
function computeExtensionProgressionFlowCore(
  flow: ProgressionFlow,
  steps: ProgressionStepStatus[],
  isExtensionOnlyJob: boolean,
  ignoreVisibility: boolean,
) {
  const isExtensionAccepted = steps.includes('ExtensionAccepted')
  const isExtensionRejected = steps.includes('ExtensionRejected')

  return computeProgressionFlowCore(flow, steps, ignoreVisibility).flatMap(
    (s) => {
      switch (s.step) {
        case 'ProgressionWaitingOnClient':
          // Hide the ProgressionWaitingOnClient step if the job is an
          // extension-only job. Since tax filing is not applicable for
          // extension-only jobs.
          if (isExtensionOnlyJob) {
            return []
          }
          return [s]

        case 'ExtensionAccepted':
          // If recorded steps include the extension rejected, remove the extension accepted step.
          if (isExtensionRejected) {
            return []
          }

          // If recorded steps does not include extension accepted/rejected step,
          // override the content of extension accepted step to indicate that the
          // extension could be accepted/rejected.
          if (!isExtensionAccepted && !isExtensionRejected) {
            return [
              {
                ...s,
                content: {
                  ...s.content,
                  title: 'Extension accepted/rejected',
                  description:
                    'This step will be updated once the Pro receives the extension status from the IRS.',
                },
              },
            ]
          }
          return [s]

        default:
          return [s]
      }
    },
  )
}

/**
 * Gets the content for the current step in the extension progression flow.
 */
export function getComputedExtensionCurrentStepContent(
  current: ProgressionStepStatus | null,
  allSteps: ComputedProgressionFlowStep[],
) {
  const step = allSteps.find((s) => s.step === current)

  // If the step is WaitingOnClient, return the content from either the
  // extension accepted or rejected step.
  if (current === 'ProgressionWaitingOnClient') {
    const acceptedOrRejectedStep = allSteps.find(
      (s) =>
        s.state === 'Completed' &&
        (s.step === 'ExtensionAccepted' || s.step === 'ExtensionRejected'),
    )

    return acceptedOrRejectedStep?.content ?? step?.content
  }

  return step?.content
}
